import { useEffect, useState } from "react";
import { t } from "i18next";
import "../../translations/i18n";
const DISALLOWED_IN_NAME = /[^A-Za-z0-9 _.-]/g;
/** Same set, without /g, so .test() per character is not stateful. */
const DISALLOWED_CHAR = /[^A-Za-z0-9 _.-]/;

const RESERVED_DEVICE_NAME = /^(con|prn|aux|nul|com[0-9]|lpt[0-9])(\.|$)/i;
export const isReservedReportName = (cleaned) =>
  !!cleaned && RESERVED_DEVICE_NAME.test(cleaned);

export const sanitizeReportName = (raw) => {
  if (!raw) return "";
  let out = String(raw)
    .trim()
    .replace(/ /g, "_")
    .replace(/[^A-Za-z0-9_.-]/g, "")
    .replace(/_{2,}/g, "_")
    .replace(/^[._-]+/, "")
    .replace(/[._-]+$/, "");
  if (out.length > 100) out = out.substring(0, 100);
  return out;
};

const DownloadReportModal = (props) => {
  const { isOpen, defaultName, onCancel, onConfirm } = props;
  const [name, setName] = useState("");
  // Characters stripped from the latest keystroke or paste, shown back to the
  // user so a vanishing character is never a mystery.
  const [rejected, setRejected] = useState("");
  useEffect(() => {
    if (isOpen) {
      setName("");
      setRejected("");
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const cleaned = sanitizeReportName(name);
  const typedSomething = name.trim().length > 0;
  const isInvalid =
    typedSomething && (cleaned.length === 0 || isReservedReportName(cleaned));

  const styles = {
    overlay:
      "modal fade fixed z-50 w-screen inset-0 bg-[#000000] bg-opacity-40 h-screen flex place-items-center justify-center outline-none",
    card: "bg-white rounded-lg shadow-md w-[460px] max-w-[92vw] p-6",
    title: "text-lg font-bold text-Dove-Gray",
    label: "block text-sm font-bold text-Dove-Gray mt-5 mb-1",
    input:
      "w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gray-500",
    hint: "text-xs text-gray-500 mt-2 break-all",
    error: "text-xs text-red-600 mt-2",
    actions: "mt-6 flex justify-end gap-3",
    cancel:
      "px-5 py-2 rounded-full border border-gray-300 text-sm text-Dove-Gray",
    confirm:
      "px-5 py-2 rounded-full bg-red-600 text-white text-sm disabled:opacity-50",
  };

  const submit = () => {
    if (isInvalid) return;
    onConfirm(cleaned);
  };

  return (
    <div className={styles.overlay}>
      <div className={styles.card}>
        <div className="flex justify-between items-center">
          <span className={styles.title}>{t("_Download_Report")}</span>
          <button
            type="button"
            className="text-gray-400 text-xl leading-none"
            onClick={onCancel}
            aria-label={t("_Cancel")}
          >
            &times;
          </button>
        </div>

        <label className={styles.label} htmlFor="rptName">
          {t("_Report_Name")}{" "}
          <span className="font-normal text-gray-400">({t("_Optional")})</span>
        </label>
        <input
          id="rptName"
          type="text"
          className={styles.input}
          value={name}
          maxLength={100}
          autoComplete="off"
          placeholder={defaultName || ""}
          onChange={(e) => {
            const raw = e.target.value;
            setName(raw.replace(DISALLOWED_IN_NAME, ""));
            const removed = Array.from(
              new Set(Array.from(raw).filter((ch) => DISALLOWED_CHAR.test(ch)))
            );
            setRejected(removed.join(" "));
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") submit();
          }}
        />

        {rejected ? (
          <div className={styles.error}>
            {t("_Report_Name_Char_Removed")} {rejected}
          </div>
        ) : null}

        {isInvalid ? (
          <div className={styles.error}>{t("_Report_Name_Invalid")}</div>
        ) : cleaned ? (
          <div className={styles.hint}>
            {t("_Report_Name_Will_Save_As")}: <b>{cleaned}.zip</b>
          </div>
        ) : (
          <div className={styles.hint}>
            {t("_Report_Name_Hint")}
            {defaultName ? (
              <>
                : <b>{defaultName}</b>
              </>
            ) : null}
          </div>
        )}

        <div className={styles.hint}>{t("_Report_Name_Allowed")}</div>

        <div className={styles.actions}>
          <button type="button" className={styles.cancel} onClick={onCancel}>
            {t("_Cancel")}
          </button>
          <button
            type="button"
            className={styles.confirm}
            disabled={isInvalid}
            onClick={submit}
          >
            {t("_Download")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DownloadReportModal;
